
import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  Spinner,
  SimpleGrid,
  Card,
  CardBody,
  Flex,
  Icon,
  Tag,
  TagLabel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useColorModeValue,
} from "@chakra-ui/react";
import { fetchWithAuth } from "../utils/fetchWithAuth";

const AdminFeatures = ({ standalone = false }) => {
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const Wrapper = React.Fragment;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const featuresResponse = await fetchWithAuth('/api/core-features-registry/features');
        const featuresData = await featuresResponse.json();
        
        setFeatures(featuresData.features || {});
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return (
    <Wrapper>
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Spinner size="xl" />
      </Box>
    </Wrapper>
  );

  if (error) return (
    <Wrapper>
      <Box p={6}>
        <Text color="red.500">Error loading features: {error}</Text>
      </Box>
    </Wrapper>
  );

  const getTagColor = (tag) => {
    const colors = {
      'plan_management_view': 'blue',
      'strategy_creation': 'green',
      'ai_chat': 'purple',
      'core_layout': 'orange',
      'workflow': 'teal',
      'input': 'cyan',
      'details': 'pink',
      'documentation': 'yellow',
      'assistance': 'red',
      'embedded': 'gray',
      'routing': 'brown',
      'persistence': 'lime'
    };
    return colors[tag] || 'gray';
  };

  const featuresByProduct = Object.entries(features).reduce((acc, [featureId, feature]) => {
    const product = feature.core_product || 'uncategorized';
    if (!acc[product]) {
      acc[product] = [];
    }
    acc[product].push({ id: featureId, ...feature });
    return acc;
  }, {});

  return (
    <Wrapper>
      <Box width="100%" mx="auto" bg={bgColor} p={6}>
        <VStack align="flex-start" spacing={6} width="100%">
          <Box>
            <Heading size="xl" mb={2}>
              Core Features Registry
            </Heading>
            <Text color="gray.600">
              Explore all features, their components, capabilities, and configurations
            </Text>
          </Box>

          <SimpleGrid columns={3} spacing={4} width="100%">
            <Card>
              <CardBody textAlign="center">
                <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                  {Object.keys(features).length}
                </Text>
                <Text fontSize="sm" color="gray.600">Total Features</Text>
              </CardBody>
            </Card>
            <Card>
              <CardBody textAlign="center">
                <Text fontSize="2xl" fontWeight="bold" color="green.500">
                  {Object.keys(featuresByProduct).length}
                </Text>
                <Text fontSize="sm" color="gray.600">Core Products</Text>
              </CardBody>
            </Card>
            <Card>
              <CardBody textAlign="center">
                <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                  {Object.values(features).reduce((acc, feature) => acc + (feature.capabilities?.length || 0), 0)}
                </Text>
                <Text fontSize="sm" color="gray.600">Total Capabilities</Text>
              </CardBody>
            </Card>
          </SimpleGrid>

          <Accordion allowToggle width="100%">
            {Object.entries(featuresByProduct).map(([productId, productFeatures]) => (
              <AccordionItem key={productId}>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <HStack>
                      <Heading size="md">
                        {productId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Heading>
                      <Badge colorScheme="blue" ml={2}>
                        {productFeatures.length} features
                      </Badge>
                    </HStack>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {productFeatures.map((feature) => (
                      <Card key={feature.id} border="1px" borderColor={borderColor}>
                        <CardBody>
                          <VStack align="start" spacing={3}>
                            <HStack justify="space-between" width="100%">
                              <Heading size="sm">{feature.name}</Heading>
                              {feature.button_config && (
                                <Icon className="material-icons-outlined" fontSize="20px">
                                  {feature.button_config.icon}
                                </Icon>
                              )}
                            </HStack>
                            
                            <Text fontSize="sm" color="gray.600">
                              {feature.description}
                            </Text>
                            
                            <Box>
                              <Text fontSize="xs" color="gray.500" mb={1}>
                                Component: {feature.component}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                Path: {feature.component_path}
                              </Text>
                            </Box>

                            {feature.tags && (
                              <Flex wrap="wrap" gap={1}>
                                {feature.tags.map((tag) => (
                                  <Tag key={tag} size="sm" colorScheme={getTagColor(tag)}>
                                    <TagLabel>{tag}</TagLabel>
                                  </Tag>
                                ))}
                              </Flex>
                            )}

                            {feature.capabilities && (
                              <Box>
                                <Text fontSize="xs" fontWeight="bold" mb={1}>
                                  Capabilities ({feature.capabilities.length}):
                                </Text>
                                <Flex wrap="wrap" gap={1}>
                                  {feature.capabilities.slice(0, 5).map((capability) => (
                                    <Badge key={capability} size="sm" variant="outline">
                                      {capability}
                                    </Badge>
                                  ))}
                                  {feature.capabilities.length > 5 && (
                                    <Badge size="sm" colorScheme="gray">
                                      +{feature.capabilities.length - 5} more
                                    </Badge>
                                  )}
                                </Flex>
                              </Box>
                            )}

                            {feature.ai_capabilities && (
                              <Box>
                                <Text fontSize="xs" fontWeight="bold" mb={1}>
                                  AI Capabilities:
                                </Text>
                                <VStack align="start" spacing={1}>
                                  <HStack>
                                    <Badge colorScheme="green">
                                      {Object.values(feature.ai_capabilities).filter(cap => cap.enabled).length} enabled
                                    </Badge>
                                    <Badge colorScheme="red">
                                      {Object.values(feature.ai_capabilities).filter(cap => !cap.enabled).length} disabled
                                    </Badge>
                                  </HStack>
                                  <Text fontSize="xs" color="gray.500">
                                    {Object.keys(feature.ai_capabilities).slice(0, 3).join(', ')}
                                    {Object.keys(feature.ai_capabilities).length > 3 && '...'}
                                  </Text>
                                </VStack>
                              </Box>
                            )}

                            {feature.ai_understanding && (
                              <Box>
                                <Text fontSize="xs" fontWeight="bold" mb={1}>
                                  AI Understanding:
                                </Text>
                                <Text fontSize="xs" color="blue.600" noOfLines={2}>
                                  {feature.ai_understanding.primary_purpose}
                                </Text>
                              </Box>
                            )}

                            {feature.button_config && (
                              <Box>
                                <Text fontSize="xs" fontWeight="bold" mb={1}>
                                  Button Config:
                                </Text>
                                <HStack spacing={1}>
                                  <Badge size="xs" colorScheme="purple">{feature.button_config.id}</Badge>
                                  <Badge size="xs" colorScheme="gray">{feature.button_config.icon}</Badge>
                                </HStack>
                              </Box>
                            )}
                          </VStack>
                        </CardBody>
                      </Card>
                    ))}
                  </SimpleGrid>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </VStack>
      </Box>
    </Wrapper>
  );
};

export default AdminFeatures;
